import { create } from "zustand";
import type {
  RewardClaimsResponse,
  RewardProgramsResponse,
  RewardRulesResponse,
} from "@/types/rewards";

export interface RewardsState {
  rewardPrograms: RewardProgramsResponse | null;
  rewardProgramsSuccess: boolean | undefined;
  createRewardProgramSuccess: boolean | undefined;
  updateRewardProgramSuccess: boolean | undefined;

  rewardRules: RewardRulesResponse | null;
  rewardRulesSuccess: boolean | undefined;
  createRewardRuleSuccess: boolean | undefined;
  updateRewardRuleSuccess: boolean | undefined;

  rewardClaims: RewardClaimsResponse | null;
  rewardClaimsSuccess: boolean | undefined;
}

interface RewardsActions {
  setRewardPrograms: (programs: RewardProgramsResponse | null) => void;
  setRewardProgramsSuccess: (success: boolean | undefined) => void;
  setCreateRewardProgramSuccess: (success: boolean | undefined) => void;
  setUpdateRewardProgramSuccess: (success: boolean | undefined) => void;

  setRewardRules: (rules: RewardRulesResponse | null) => void;
  setRewardRulesSuccess: (success: boolean | undefined) => void;
  setCreateRewardRuleSuccess: (success: boolean | undefined) => void;
  setUpdateRewardRuleSuccess: (success: boolean | undefined) => void;

  setRewardClaims: (claims: RewardClaimsResponse | null) => void;
  setRewardClaimsSuccess: (success: boolean | undefined) => void;

  reset: () => void;
}

const initialState: RewardsState = {
  rewardPrograms: null,
  rewardProgramsSuccess: undefined,
  createRewardProgramSuccess: undefined,
  updateRewardProgramSuccess: undefined,
  rewardRules: null,
  rewardRulesSuccess: undefined,
  createRewardRuleSuccess: undefined,
  updateRewardRuleSuccess: undefined,
  rewardClaims: null,
  rewardClaimsSuccess: undefined,
};

export const useRewardsStore = create<RewardsState & RewardsActions>((set) => ({
  ...initialState,

  setRewardPrograms: (programs) => set({ rewardPrograms: programs }),
  setRewardProgramsSuccess: (success) =>
    set({ rewardProgramsSuccess: success }),
  setCreateRewardProgramSuccess: (success) =>
    set({ createRewardProgramSuccess: success }),
  setUpdateRewardProgramSuccess: (success) =>
    set({ updateRewardProgramSuccess: success }),

  setRewardRules: (rules) => set({ rewardRules: rules }),
  setRewardRulesSuccess: (success) => set({ rewardRulesSuccess: success }),
  setCreateRewardRuleSuccess: (success) =>
    set({ createRewardRuleSuccess: success }),
  setUpdateRewardRuleSuccess: (success) =>
    set({ updateRewardRuleSuccess: success }),

  setRewardClaims: (claims) => set({ rewardClaims: claims }),
  setRewardClaimsSuccess: (success) => set({ rewardClaimsSuccess: success }),

  reset: () => set(initialState),
}));

export const useRewardPrograms = () =>
  useRewardsStore((state) => state.rewardPrograms);
export const useRewardProgramsSuccess = () =>
  useRewardsStore((state) => state.rewardProgramsSuccess);
export const useCreateRewardProgramSuccess = () =>
  useRewardsStore((state) => state.createRewardProgramSuccess);
export const useUpdateRewardProgramSuccess = () =>
  useRewardsStore((state) => state.updateRewardProgramSuccess);
export const useRewardRules = () =>
  useRewardsStore((state) => state.rewardRules);
export const useRewardRulesSuccess = () =>
  useRewardsStore((state) => state.rewardRulesSuccess);
export const useCreateRewardRuleSuccess = () =>
  useRewardsStore((state) => state.createRewardRuleSuccess);
export const useUpdateRewardRuleSuccess = () =>
  useRewardsStore((state) => state.updateRewardRuleSuccess);
export const useRewardClaims = () =>
  useRewardsStore((state) => state.rewardClaims);
export const useRewardClaimsSuccess = () =>
  useRewardsStore((state) => state.rewardClaimsSuccess);
